import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { type AnalyticsFilter } from '../../services/api/analytics';
import { QUICK_FILTERS, MONTH_NAMES } from './analytics.constants';

const Dropdown = ({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: { label: string; value: number }[];
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors
          ${
            disabled
              ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400'
              : value != null
                ? 'bg-brand-blue text-white border-brand-blue shadow'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}
      >
        {selected ? selected.label : label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[140px] rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-slate-50
                ${opt.value === value ? 'font-semibold text-brand-blue bg-blue-50' : 'text-slate-700'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface Props {
  filter: AnalyticsFilter;
  availableYears: number[];
  selectedYear: number | null;
  selectedMonth: number | null;
  onQuickFilter: (days: number) => void;
  onYearSelect: (year: number) => void;
  onMonthSelect: (month: number) => void;
  periodLabel: string;
  loading: boolean;
}

export const AnalyticsFilterBar = ({
  filter,
  availableYears,
  selectedYear,
  selectedMonth,
  onQuickFilter,
  onYearSelect,
  onMonthSelect,
  periodLabel,
  loading,
}: Props) => {
  const activeQuickDays = filter.mode === 'days' ? filter.days : null;

  const monthOptions = (() => {
    if (!selectedYear) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return MONTH_NAMES.map((label, i) => ({ label, value: i + 1 })).filter(
      (m) => selectedYear < currentYear || m.value <= currentMonth,
    );
  })();

  return (
    <div className="flex items-start justify-between flex-wrap gap-4">
      <div>
        <h2 className="text-3xl font-semibold text-brand-blue font-display">Analytics</h2>
        <p className="text-sm text-text-grey mt-0.5">{loading ? 'Laddar...' : periodLabel}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {QUICK_FILTERS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => onQuickFilter(opt.days)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors
                ${
                  activeQuickDays === opt.days
                    ? 'bg-brand-blue text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {availableYears.length > 0 && (
          <Dropdown
            label="Välj år"
            options={availableYears.map((y) => ({ label: String(y), value: y }))}
            value={selectedYear}
            onChange={onYearSelect}
          />
        )}

        {selectedYear != null && (
          <Dropdown
            label="Välj månad"
            options={monthOptions}
            value={selectedMonth}
            onChange={onMonthSelect}
            disabled={monthOptions.length === 0}
          />
        )}
      </div>
    </div>
  );
};
